import cn from 'classnames';
import {useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from 'app/router/routes';
import {
	HowToBecomeStatusDrawer,
	PartnerAgreementFrame,
	RegistrationConfirmationModal,
	RegistrationStep,
	SnilsErrorModal,
	WORK_STATUS_STEP_ENABLED,
} from 'features/registration';
import {RegistrationStepContent} from './RegistrationStepContent';
import {RegistrationWizardFooters} from './RegistrationWizardFooters';
import {RegistrationWizardHeader} from './RegistrationWizardHeader';
import {useIdentityReviewEdit} from './useIdentityReviewEdit';
import {useRegistrationBackNavigation} from './useRegistrationBackNavigation';
import {useRegistrationDerived} from './useRegistrationDerived';
import {useRegistrationEffects} from './useRegistrationEffects';
import {usePersonalInfoPrefill} from './usePersonalInfoPrefill';
import {usePassportDetailsPrefill} from './usePassportDetailsPrefill';
import {useRegistrationPatchers} from './useRegistrationPatchers';
import {useRegistrationPaymentFlow} from './useRegistrationPaymentFlow';
import {useRegistrationPhotosEditEntry} from './useRegistrationPhotosEditEntry';
import {useRegistrationProgressRestore} from './useRegistrationProgressRestore';
import {useRegistrationStatusesEditEntry} from './useRegistrationStatusesEditEntry';
import {useRegistrationSubmit} from './useRegistrationSubmit';
import {useRegistrationWizardState, type RegistrationWizardEntry} from './useRegistrationWizardState';
import {useStatusConnectionFlow} from './useStatusConnectionFlow';
import styles from './RegistrationWizard.module.css';

type Props = {
	entry?: RegistrationWizardEntry;
};

export const RegistrationWizard = ({entry}: Props) => {
	const navigate = useNavigate();
	const state = useRegistrationWizardState(entry);

	const handleClose = () => {
		if (state.openedFromProfileAtStatuses) {
			navigate(ROUTES.profile, {replace: true});
			return;
		}

		navigate('/orders', {replace: true});
	};

	const derived = useRegistrationDerived(state);
	const identityEdit = useIdentityReviewEdit(state, {
		onCancelWithoutSnapshot: () => {
			navigate(ROUTES.profile, {replace: true});
		},
	});
	const patchers = useRegistrationPatchers(state);

	useRegistrationEffects(state);
	usePersonalInfoPrefill(state);
	usePassportDetailsPrefill(state, derived.usesForeignDocumentDetails);
	useRegistrationPhotosEditEntry(state);
	useRegistrationStatusesEditEntry(state);

	const statusConnection = useStatusConnectionFlow({
		state,
		postInnFlow: derived.postInnFlow,
		handleClose,
	});
	const payment = useRegistrationPaymentFlow({
		state,
		hasExistingPaymentWays: derived.hasExistingPaymentWays,
		completeStatusConnection: statusConnection.completeStatusConnection,
	});
	const submit = useRegistrationSubmit({
		state,
		derived,
		statusConnection,
		payment,
		clearIdentityEditSnapshot: identityEdit.clearIdentityEditSnapshot,
		handleClose,
	});
	/** Заголовок экрана статусов — «Подключим ещё?»: крестик закрывает без подтверждения. */
	const connectMoreRef = useRef(false);

	const handleBack = useRegistrationBackNavigation({
		state,
		isEditingFromIdentityReview: derived.isEditingFromIdentityReview,
		cancelIdentityReviewEdit: identityEdit.cancelIdentityReviewEdit,
		handleClose,
	});
	const isProgressReady = useRegistrationProgressRestore(state);

	if (!isProgressReady) {
		return null;
	}

	/** Обработка крестика в шапке в зависимости от текущего шага. */
	const handleHeaderClose = () => {
		if (derived.isEditingFromIdentityReview) {
			identityEdit.cancelIdentityReviewEdit();
			return;
		}

		if (state.step === RegistrationStep.WorkStatus && connectMoreRef.current) {
			handleClose();
			return;
		}

		state.setExitConfirmKind(
			WORK_STATUS_STEP_ENABLED && derived.isStatusConnectionFlow ? 'status' : 'registration',
		);
	};

	const confirmExitModal = () => {
		const kind = state.exitConfirmKind;

		state.setExitConfirmKind(null);

		if (kind === 'status') {
			statusConnection.exitStatusConnectionToWorkStatus();
			return;
		}

		handleClose();
	};

	return (
		<div className={cn(styles.root, derived.isIdentityReviewStep && styles.root_review)}>
			<div
				className={cn(
					styles.shell,
					derived.isIdentityReviewStep && styles.shell_review,
					derived.isMyTaxIntroStep && styles.shell_intro,
					derived.isMyTaxSuccessStep && styles.shell_success,
					derived.isRegistrationCompleteStep && styles.shell_complete,
					(derived.isMyTaxAccessErrorStep ||
						derived.isMyTaxNotSelfEmployedStep ||
						derived.isMyTaxLoadErrorStep) &&
						styles.shell_error,
				)}
			>
				{derived.isRegistrationCompleteStep ? null : (
					<RegistrationWizardHeader
						useCloseHeader={derived.useCloseHeader}
						headerProgress={derived.headerProgress}
						isEditingFromIdentityReview={derived.isEditingFromIdentityReview}
						onBack={handleBack}
						onClose={handleHeaderClose}
					/>
				)}

				<div className={styles.contentBlock}>
					<div className={styles.main}>
						<RegistrationStepContent
							state={state}
							derived={derived}
							patchers={patchers}
							onWorkStatusSelect={statusConnection.handleWorkStatusSelect}
							onSelectFreeSmz={statusConnection.handleFreeSmzSelect}
							onOpenAgreement={statusConnection.openPartnerAgreement}
							onConnectMoreChange={(connectMore) => {
								connectMoreRef.current = connectMore;
							}}
							onPaymentMethodSelect={payment.handlePaymentMethodSelect}
							onEditIdentity={identityEdit.beginIdentityReviewEdit}
							onRestartFromCitizenship={identityEdit.restartFromCitizenship}
							onRestartFromResidence={identityEdit.restartFromResidence}
						/>
					</div>

					<RegistrationWizardFooters
						step={state.step}
						workStatus={state.workStatus}
						postInnFlow={derived.postInnFlow}
						isSubmitting={state.isSubmitting}
						myTaxFooterLoading={state.myTaxFooterLoading}
						isRegistrationCompleteStep={derived.isRegistrationCompleteStep}
						isIdentityReviewStep={derived.isIdentityReviewStep}
						showResidentCodeFooter={derived.showResidentCodeFooter}
						canSkipResidentCode={derived.canSkipResidentCode}
						residentCodeContinueVisible={derived.residentCodeContinueVisible}
						showContinueFooter={derived.showContinueFooter}
						continueButtonLabel={derived.continueButtonLabel}
						onOpenHowToBecome={() => state.setIsHowToBecomeOpen(true)}
						onMyTaxIntroSecondary={statusConnection.handleMyTaxIntroSecondary}
						onMyTaxIntroProceed={() => void statusConnection.handleMyTaxIntroProceed()}
						onGoToMyTax={() => void statusConnection.handleGoToMyTax()}
						onAccessGranted={() => void statusConnection.handleAccessGranted()}
						onCompleteStatusConnection={statusConnection.completeStatusConnection}
						onMyTaxNameMismatchConfirm={statusConnection.handleMyTaxNameMismatchConfirm}
						onMyTaxAccessErrorRetry={statusConnection.handleMyTaxAccessErrorRetry}
						onMyTaxNotSelfEmployedCheckInn={statusConnection.handleMyTaxNotSelfEmployedCheckInn}
						onFnsPendingContinue={statusConnection.handleFnsPendingContinue}
						onMyTaxLoadErrorRetry={statusConnection.handleMyTaxLoadErrorRetry}
						onMyTaxLoadErrorBack={handleBack}
						onClose={handleClose}
						onResidentCodeSkip={() => void submit.handleResidentCodeSkip()}
						onContinue={() => void submit.handleContinue()}
					/>
				</div>
			</div>

			<HowToBecomeStatusDrawer
				open={state.isHowToBecomeOpen}
				workStatus={state.workStatus}
				onClose={() => state.setIsHowToBecomeOpen(false)}
			/>

			<SnilsErrorModal
				isOpen={submit.snilsError !== null}
				message={submit.snilsError ?? ''}
				onClose={submit.closeSnilsError}
			/>

			<RegistrationConfirmationModal
				isOpen={state.exitConfirmKind !== null}
				title={state.exitConfirmKind === 'status' ? 'Хотите выйти?' : 'Выйти из регистрации?'}
				description={
					state.exitConfirmKind === 'status'
						? 'Вы уверены, что хотите прервать подключение статуса?'
						: 'Чтобы открыть все возможности приложения, нужно её закончить'
				}
				confirmText="Выйти"
				onConfirm={confirmExitModal}
				onCancel={() => state.setExitConfirmKind(null)}
			/>

			{derived.postInnFlow.intro.confirm ? (
				<RegistrationConfirmationModal
					isOpen={statusConnection.isAlfaStatusConfirmOpen}
					title={derived.postInnFlow.intro.confirm.title}
					description={derived.postInnFlow.intro.confirm.description}
					confirmText={derived.postInnFlow.intro.proceedLabel}
					onConfirm={statusConnection.handleAlfaStatusConfirm}
					onCancel={statusConnection.handleAlfaStatusConfirmCancel}
				/>
			) : null}

			{statusConnection.partnerAgreementLink ? (
				<PartnerAgreementFrame
					url={statusConnection.partnerAgreementLink}
					onClose={statusConnection.handlePartnerAgreementClose}
				/>
			) : null}
		</div>
	);
};
